import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import {
  ISbStories,
  ISbStoriesParams,
  ISbStory,
  ISbStoryData,
  ISbStoryParams,
  StoryblokBridgeV2,
  renderRichText,
  storyblokInit,
} from '@storyblok/js';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  catchError,
  from,
  lastValueFrom,
  of,
  share,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import { getAllRouteData } from '../lib/get-all-route-data';
import { getFullPath } from '../lib/get-full-path';
import { isIframe } from '../lib/is-iframe';
import { loadScript } from '../lib/load-script';
import { getRelationships } from '../storyblok/storyblok-find-components';
import { BrowserService } from './browser.service';
import { TransferStateService } from './transfer-state.service';

declare global {
  interface Window {
    storyblok: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class StoryblokService {
  private route = inject(ActivatedRoute);
  accessToken = 'qit3qOMDswj7KNAg95r4yAtt';
  version: 'published' | 'draft' = 'published';
  linkMap = new Map<string, string>();

  tempUpdateCache = new Map<string, any>();

  // not using the client because it is super slow on the server
  private stories$ = new Map<string, Observable<ISbStory['data']>>();
  private storiesByType$ = new Map<string, Observable<ISbStoryData[]>>();
  cacheVersion: number = undefined;

  private _update: Subject<ISbStoryData | undefined> = new BehaviorSubject<ISbStoryData | undefined>(undefined);
  public readonly onUpdates = this._update.asObservable();
  storyblokInstance: StoryblokBridgeV2;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private transferState: TransferStateService,
    private http: HttpClient,
    private browserService: BrowserService
  ) {
    storyblokInit(); // can change in the future to add support for custom resolvers
    if (isPlatformServer(this.platformId)) return;
    if (!isIframe()) return;
    if (this.browserService.isExtension()) return;
    this.init();
  }

  async init() {
    await loadScript('https://app.storyblok.com/f/storyblok-v2-latest.js?t=Q2mIMuV6frKpnmQlOee0rAtt');
    console.log('Loading Storyblok JS for preview mode');

    const { StoryblokBridge, location } = window;

    this.storyblokInstance = new StoryblokBridge({
      preventClicks: true,
    });

    this.storyblokInstance.on(['change', 'published'], function (e: any) {
      location.reload();
    });

    this.storyblokInstance.on(['input'], (e: any) => {
      this.zone.run(async () => {
        this.resolveRelationships(this.route.snapshot).subscribe(relationships => {
          this.tempUpdateCache.set('relationships', relationships);
          this._update.next(e.story);
        });
      });
    });
  }

  public getGlobalData(key: string) {
    if (this.tempUpdateCache.has(key)) {
      return this.tempUpdateCache.get(key);
    }
    const routeData = getAllRouteData(this.route.snapshot);
    return routeData[key];
  }

  private getStoryUrl(slug: string, params: ISbStoryParams) {
    const query = new URLSearchParams();
    if (params.resolve_links) {
      query.set('resolve_links', params.resolve_links);
    }

    return `https://api.storyblok.com/v2/cdn/stories/${slug}?${query.toString()}&token=${this.accessToken}&version=${
      params.version || 'published'
    }&cv=${this.cacheVersion}`;
  }

  private getStoryObservable(slug: string, params: ISbStoryParams): Observable<ISbStory['data']> {
    return this.transferState
      .withStateTransferObservable(`storyblok-page-${slug}`, () =>
        this.http.get<ISbStory['data']>(this.getStoryUrl(slug, params))
      )
      .pipe(
        tap(res => {
          this.cacheVersion = res?.cv;
        }),
        catchError(err => {
          console.error(err);
          return <Observable<undefined>>of(undefined);
        }),
        share({
          resetOnComplete: () => timer(0),
          connector: () => new ReplaySubject(1), // override default "new Subject()"
        })
      );
  }

  getStory(
    slug: string,
    route: ActivatedRouteSnapshot = this.route.snapshot
  ): Observable<ISbStory['data'] | undefined> {
    if (!slug) return of(undefined);
    const version = route.queryParams['_storyblok'] ? 'draft' : 'published';
    if (!this.stories$.has(slug)) {
      this.stories$.set(slug, this.getStoryObservable(slug, { version }));
    }
    return this.stories$.get(slug)!;
  }

  private buildStoriesUrl(params: ISbStoriesParams) {
    const query = new URLSearchParams();
    if (params.content_type) {
      query.set('content_type', params.content_type);
    }
    if (params.starts_with) {
      query.set('starts_with', params.starts_with);
    }
    if (params.with_tag) {
      query.set('with_tag', params.with_tag);
    }
    if (params.page) {
      query.set('page', params.page.toString());
    }
    if (params.per_page) {
      query.set('per_page', params.per_page.toString());
    }
    if (params.excluding_fields) {
      query.set('excluding_fields', params.excluding_fields);
    }
    if (params.by_uuids) {
      query.set('by_uuids', params.by_uuids);
    }

    if (params.filter_query) {
      params.filter_query.forEach(item => {
        query.set(`filter_query[${item.key}][in]`, item.value);
      });
    }

    return `https://api.storyblok.com/v2/cdn/stories?${query.toString()}&token=${this.accessToken}&version=${
      params.version || 'published'
    }&cv=${this.cacheVersion}&resolve_links=url`;
  }

  private async getAllStories(params: ISbStoriesParams, page = 1, results = []): Promise<ISbStoryData[]> {
    const resultsPerPage = 100;
    const url = this.buildStoriesUrl({ ...params, page, per_page: resultsPerPage });
    const resp = await this.transferState.withStateTransfer(url, async () =>
      lastValueFrom(this.http.get<ISbStories['data']>(url))
    );

    resp.links.forEach(link => {
      this.linkMap.set(link.uuid, link['url']);
    });

    if (resp.stories.length === resultsPerPage) {
      return this.getAllStories(params, page + 1, [...results, ...resp.stories]);
    }
    return [...results, ...resp.stories];
  }

  private getStoriesObservable(params: ISbStoriesParams): Observable<ISbStoryData[]> {
    return from(this.getAllStories(params)).pipe(
      catchError(err => {
        console.error(err);
        return <Observable<undefined>>of(undefined);
      }),
      share({
        resetOnComplete: () => timer(0),
        connector: () => new ReplaySubject(1), // override default "new Subject()"
      })
    );
  }

  getStories(
    options: ISbStoriesParams,
    route: ActivatedRouteSnapshot = this.route.snapshot
  ): Observable<ISbStoryData[]> {
    const version = route.queryParams['_storyblok'] ? 'draft' : 'published';
    const cacheKey = JSON.stringify({ ...options, version });
    if (!this.storiesByType$.has(cacheKey)) {
      this.storiesByType$.set(cacheKey, this.getStoriesObservable({ ...options, version }));
    }
    return this.storiesByType$.get(cacheKey)!;
  }

  public resolveRelationships(route: ActivatedRouteSnapshot) {
    const path = getFullPath(route).join('/') || 'home';
    return this.getStory(path, route).pipe(
      switchMap(async resp => {
        const story = resp?.story;
        if (!story) return [];

        let ids = Array.from(new Set(getRelationships(story))).filter(Boolean);
        const allStories = [story];

        while (ids.length) {
          const stories = await lastValueFrom(
            this.getStories(
              { by_uuids: ids.join(','), excluding_fields: 'body,preFooter,topBlur,blurOverlay' }, // excluding page fields because of previews where we anyway only need the meta data
              route
            )
          );

          ids = [];
          stories.forEach(_story => {
            ids.push(...getRelationships(_story));
          });
          ids = Array.from(new Set(ids)).filter(Boolean);
          allStories.push(...stories);
        }

        return allStories;
      })
    );
  }

  private getBlocks(items: any[], components: string[], returnEarly = false) {
    const blocks = [];
    if (!items) return blocks;
    for (let item of items) {
      if (components.includes(item.component)) {
        blocks.push(item);
        if (returnEarly) return blocks;
      }
      for (const key in item) {
        if (Array.isArray(item[key])) {
          blocks.push(...this.getBlocks(item[key], components));
          if (returnEarly && blocks.length) return blocks;
        }
      }
    }
    return blocks;
  }

  public getStoryTitle(story: ISbStoryData): string {
    if (story.content.metaTitle) return story.content.metaTitle;
    const block = this.getBlocks(story.content.body, ['Title'], true)[0];
    return `${block?.title ?? story.name} · Picflow`;
  }

  public getStoryDescription(story: ISbStoryData): string {
    if (story.content.metaDescription) return story.content.metaDescription;
    const block = this.getBlocks(story.content.body, ['Text'], true)[0];
    if (!block?.content) {
      return 'The next-generation online gallery and proofing platform to boost creative workflows for freelancers and teams.';
    }
    return renderRichText(block.content).replace(/<[^>]*>?/gm, '');
  }

  public getUrlLink(link: any) {
    if (!link) return '';
    if (link.linktype === 'url') return link.url;
    if (link.linktype === 'story' && link.cached_url) {
      return '/' + (this.linkMap.get(link.id) || link.cached_url);
    }
    return '';
  }
}
